import React, { useEffect, useState } from 'react';
import ListItemButton from '@mui/material/ListItemButton';
import { Button } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';
import { RadioButtonGroup } from 'framework/form';
import PrepopulateFormDataLayout from 'domain/people/employee/prepopulation/PrepopulateFormDataLayout';
import { usePrepopulationContext, PrepopulationForm } from 'domain/people/employee/prepopulation/PrepopulationContext';
import { Box, SxProps, Theme, Typography } from '@mui/material';

type CallbackData = {
  [source: string]: any;
};

type PrepopulateButtonProps = {
  scrollToPrepopulatedInputs?: () => void;
  sx?: SxProps<Theme>;
};

const PrepopulateButton = ({ scrollToPrepopulatedInputs, sx }: PrepopulateButtonProps) => {
  const { forms = [], loadForm, loading } = usePrepopulationContext();
  const [isModalOpen, setModalOpen] = useState(false);
  const isSingleFormAvailable = forms.length === 1;
  const initialSelectedFormId = forms.length >= 1 ? forms[0].id : null;
  const [selectedFormId, setSelectedFormId] = useState<Nullable<number | string>>(null);

  useEffect(() => {
    setSelectedFormId(initialSelectedFormId);
  }, [forms, initialSelectedFormId]);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedFormId(initialSelectedFormId);
  };

  const prepopulateForm = async () => {
    await loadForm(selectedFormId);

    setModalOpen(false);
    setSelectedFormId(initialSelectedFormId);

    if (scrollToPrepopulatedInputs) {
      scrollToPrepopulatedInputs();
    }
  };

  const getOptionLabel = (form: PrepopulationForm) => {
    return (
      <ListItemButton
        key={form.id}
        selected={form.id === selectedFormId}
        onClick={e => {
          e.preventDefault();
          setSelectedFormId(form.id);
        }}
      >
        <PrepopulateFormDataLayout form={form} />
      </ListItemButton>
    );
  };

  const getOptions = () =>
    forms.map(form => ({
      value: form.id,
      label: getOptionLabel(form),
    }));

  const handleChange = (data: CallbackData) => {
    setSelectedFormId(data.form);
  };

  return (
    <Box>
      {forms && forms.length ? (
        <Button
          onClick={openModal}
          sx={[{ marginLeft: '10px', marginTop: '12px' }, ...(Array.isArray(sx) ? sx : [sx])]}
          size="small"
        >
          Prepopulate form
        </Button>
      ) : null}
      <Modal
        isOpen={isModalOpen}
        title={isSingleFormAvailable ? 'Form prepopulation' : 'Choose form for prepopulation'}
        buttons={[
          <Button
            key="populate-button"
            onClick={prepopulateForm}
            disabled={!selectedFormId || loading}
            id="prepopulate-button"
          >
            Populate
          </Button>,
        ]}
        onToggle={closeModal}
      >
        <Typography variant="body1" mb={1}>
          Here is the information available for prepopulating this form:
        </Typography>
        {isSingleFormAvailable ? (
          <PrepopulateFormDataLayout form={forms[0]} />
        ) : (
          <RadioButtonGroup
            options={getOptions()}
            value={selectedFormId}
            source="form"
            label=""
            onChangeCallback={handleChange}
            isColumnDirection
            sx={{
              marginTop: '-8px',
              '& .MuiFormGroup-root, & .MuiFormControlLabel-root': { width: '100%' },
              '& .MuiFormControlLabel-root': {
                margin: 0,
                marginBottom: '12px',
                '&:last-of-type': { marginBottom: 0 },
              },
              '& .MuiFormControlLabel-label': { flex: 1 },
              '& .MuiListItemButton-root': {
                border: '1px solid',
                borderColor: 'border.default',
                borderRadius: '5px',
                outlineColor: 'info.dark',
                '&.Mui-selected': {
                  color: 'info.main',
                  backgroundColor: 'common.white',
                  borderColor: 'info.main',
                },
              },
            }}
          />
        )}
      </Modal>
    </Box>
  );
};

export default PrepopulateButton;
